/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import React from 'react';
import { Check, Close, MapOutlined } from '@mui/icons-material';
import { Box, Button, ButtonBase, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import { useAtom } from 'jotai';
import {
  BASEMAPS,
  BASEMAP_PROVIDERS,
  basemapThumbnailUrl,
  mapStyleAtom,
  type BasemapOption,
} from './basemaps';

const ACCENT = 'rgb(0, 82, 136)';

type BasemapCardProps = {
  basemap: BasemapOption;
  selected: boolean;
  onSelect: (key: string) => void;
};

const BasemapCard = React.memo(({ basemap, selected, onSelect }: BasemapCardProps) => {
  const [failed, setFailed] = React.useState(false);
  const thumbnailUrl = basemapThumbnailUrl(basemap.key);

  return (
    <ButtonBase
      onClick={() => onSelect(basemap.key)}
      data-basemap-key={basemap.key}
      aria-label={basemap.label}
      aria-pressed={selected}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: '100%',
        p: 0.5,
        borderRadius: '8px',
        border: `2px solid ${selected ? ACCENT : 'transparent'}`,
        backgroundColor: selected ? 'rgba(0, 82, 136, 0.08)' : 'transparent',
        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
        '&:focus-visible': { outline: `2px solid ${ACCENT}` },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '4 / 3',
          borderRadius: '6px',
          overflow: 'hidden',
          backgroundColor: '#dfe3e6',
        }}
      >
        {!failed && (
          <Box
            component="img"
            src={thumbnailUrl}
            alt=""
            loading="lazy"
            onError={() => {
              console.warn(`Missing basemap thumbnail for "${basemap.key}" (${thumbnailUrl})`);
              setFailed(true);
            }}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}
        {selected && (
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: ACCENT,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 0 2px #fff',
            }}
          >
            <Check sx={{ fontSize: 14 }} />
          </Box>
        )}
      </Box>
      <Typography
        variant="body2"
        noWrap
        title={basemap.label}
        sx={{
          mt: 0.5,
          width: '100%',
          textAlign: 'center',
          lineHeight: 1.3,
          fontWeight: selected ? 700 : 400,
          color: selected ? ACCENT : 'text.primary',
        }}
      >
        {basemap.label}
      </Typography>
    </ButtonBase>
  );
});

export const MapStylePicker = React.memo(() => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [mapStyle, setMapStyle] = useAtom(mapStyleAtom);

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);

  // Selecting a basemap intentionally keeps the picker open (for quick visual
  // comparison); it closes only via the X button, Escape, or an outside click.
  const handleSelect = React.useCallback(
    (style: string) => {
      setMapStyle(style);
    },
    [setMapStyle]
  );

  return (
    <div className="style-picker">
      <Tooltip title="Choose the basemap drawn beneath the data layers" describeChild>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          aria-controls="basemap-popover"
          aria-haspopup="true"
          onClick={handleClick}
          startIcon={<MapOutlined />}
          sx={{
            backdropFilter: 'blur(10px)',
            mb: 1,
            color: 'white',
            backgroundColor: ACCENT,
          }}
        >
          Base Map
        </Button>
      </Tooltip>
      <Popover
        id="basemap-popover"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: 526,
              maxHeight: 'min(490px, 80vh)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              // Composite elevation that reads on any basemap: the light rim +
              // halo separate the panel from dark/satellite maps (where a dark
              // shadow is invisible), the dark shadows carry light maps.
              boxShadow:
                '0 0 0 2px rgba(255, 255, 255, 0.55), 0 0 24px 4px rgba(255, 255, 255, 0.22), 0 16px 48px rgba(0, 0, 0, 0.6)',
            },
          },
        }}
      >
        {/* The close button keeps its own non-scrolling strip so the scroll
            region -- and its scrollbar -- starts below it instead of running
            underneath the button. */}
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', px: 0.75, pt: 0.75, flexShrink: 0 }}
        >
          <IconButton
            aria-label="Close basemap picker"
            size="small"
            onClick={handleClose}
            sx={{
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ px: 1.5, pb: 1.5, overflowY: 'auto' }}>
          {BASEMAP_PROVIDERS.map((provider, index) => (
            <Box
              key={provider}
              sx={index > 0 ? { mt: 1.5, borderTop: '1px solid rgba(0, 0, 0, 0.08)' } : {}}
            >
              {/* Inline while its section is below the top, then sticks to the
                  top of the scroll viewport as the section scrolls past, and
                  is pushed out by the next section's heading (sticky is
                  bounded by the section box). Opaque so cards slide under it;
                  this is also what keeps every provider's name visible for
                  short sections that never reach the top themselves. */}
              <Typography
                component="h3"
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  backgroundColor: 'background.paper',
                  // Explicit size: the app theme overrides heading variants
                  // down to ~1rem, so variants cannot express a real heading.
                  fontSize: '1.375rem',
                  lineHeight: 1.3,
                  color: 'text.secondary',
                  fontWeight: 700,
                  textAlign: 'center',
                  py: 0.75,
                }}
              >
                {provider}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(158px, 1fr))',
                  gap: 1,
                }}
              >
                {BASEMAPS.filter(basemap => basemap.provider === provider).map(basemap => (
                  <BasemapCard
                    key={basemap.key}
                    basemap={basemap}
                    selected={basemap.key === mapStyle}
                    onSelect={handleSelect}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Popover>
    </div>
  );
});
